import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from "@angular/core";
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from "@angular/forms";
import { ControlBase } from "@mipro/custom-controls";
import { IRavenIssue } from "./models/raven-issue";
import { RavenNotePlaceholder } from "./models/raven-note-placeholder";
import { RavenApiService } from "./raven-api.service";
import { IRavenPriority } from "./models/raven-priority";
import { IRavenCategory } from "./models/raven-category";
import { IRavenLog } from "./models/raven-log";
import { IRavenLocation } from "./models/raven-location";
import { IRavenStatus } from "./models/raven-status";
import { ConfirmationService, MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";
import { RavenPriorityService } from "./raven-priority.service";
import { RavenCategoryService } from "./raven-category.service";
import { RavenStatusService } from "./raven-status.service";
import { IQueryCommand } from "@features/filter";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { Subject, Subscription, debounceTime, switchMap, takeUntil } from "rxjs";

@Component({
    selector: "raven-fields",
    templateUrl: "./raven-control.component.html",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: RavenControlComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: RavenControlComponent,
            multi: true
        }
    ],
    styleUrls: ["raven-control.component.scss"]
})
export class RavenControlComponent extends ControlBase<IRavenIssue> implements OnInit, OnDestroy {
    private destroyed$ = new Subject<void>();

    private onlineChangeSubscription: Subscription;

    ravenIssueId: string;
    ravenIssue: IRavenIssue = null;

    ravenModuleEnabled: boolean = false;
    ravenValidConfig: boolean = false;
    isIssueClosed: boolean = false;

    events: IRavenLog[];
    eventsOptions: IRavenLog[] = [];
    _selectedEvent: IRavenLog;
    defaultEventId: string = "-1";
    defaultEvent: boolean = false;

    private readonly selectedEvent$: Subject<IRavenLog> = new Subject<IRavenLog>();

    get selectedEvent(): IRavenLog {
        if (!this._selectedEvent) return null;

        if (!this.events) {
            this.events = [];
        }

        const alreadyExists = this.events.some(x => x.id === this._selectedEvent.id);
        if (!alreadyExists) {
            this.events.push(this._selectedEvent);
        }
        return this._selectedEvent;
    }

    set selectedEvent(value: IRavenLog) {
        this._selectedEvent = value;
    }

    archivedLog: boolean = false;

    consentInput: { label: string; value: boolean };
    consentOptions = [
        { label: "Yes", value: true },
        { label: "No", value: false }
    ];

    locationInput: IRavenLocation;
    locations: IRavenLocation[];
    locationsOptions: IRavenLocation[] = [];

    priorityInput: IRavenPriority;
    allPriorities: IRavenPriority[];
    priorityOptions: IRavenPriority[] = [];

    categoriesInput: IRavenCategory[];
    allCategories: IRavenCategory[];
    categoryOptions: IRavenCategory[] = [];

    ravenStatuses: IRavenStatus[] = [];

    initializing = false;

    isOnline: boolean = true;

    queryCommand: IQueryCommand;

    /** Guards async writeValue work so a stale create-default-event pass can't wipe edit values. */
    private writeGeneration = 0;

    private _episodeId: string;

    get episodeId() {
        return this._episodeId;
    }
    @Input() public set episodeId(value: string) {
        this._episodeId = value;
    }
    @Input() layout: "two-columns" | "horizontal" = "two-columns";
    @Input() showConsent: boolean = true;
    @Input() showNotes: boolean = false;
    @Input() skipDefaultEvent: boolean = false;

    notesInput: string;

    constructor(
        public ravenApiService: RavenApiService,
        public messageService: MessageService,
        public priorityService: RavenPriorityService,
        public categoryService: RavenCategoryService,
        public statusService: RavenStatusService,
        private confirmationService: ConfirmationService,
        private appOnlineStateService: AppOnlineStateService,
        private broadcastService: BroadcastService,
        private cd: ChangeDetectorRef
    ) {
        super();

        this.selectedEvent$
            .pipe(
                debounceTime(50),
                takeUntil(this.destroyed$),
                switchMap(it => this.doProcessEventChanged(it))
            )
            .subscribe(() => {});
    }

    ngOnDestroy(): void {
        this.onlineChangeSubscription?.unsubscribe();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    async ngOnInit() {
        this.queryCommand = <IQueryCommand>{
            filters: [],
            sortingField: "UpdateDateTime",
            sortingDirection: -1
        };

        this.ravenModuleEnabled = this.ravenApiService.isRavenModuleEnabled();

        if (this.ravenModuleEnabled) {
            this.loadOptions();
            this.getRavenStatus();
        }

        if (this.ravenModuleEnabled) {
            this.ravenValidConfig = await this.ravenApiService.ravenConfigIsValid();
            this.showWarningIfBadRavenConfig();
        }

        this.isOnline = this.appOnlineStateService.isAppOnline;

        this.onlineChangeSubscription = this.broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .subscribe(data => this.changesOnlineStatus(data));

        if (this.showNotes && this.ravenIssue && !this.ravenIssue.ravenNotes) {
            this.ravenIssue.ravenNotes = RavenNotePlaceholder;
            this.notesInput = this.ravenIssue.ravenNotes;
            this.onChange(this.ravenIssue);
        }
    }

    writeValue(obj: IRavenIssue): void {
        const generation = ++this.writeGeneration;

        if (obj === undefined || obj === null) {
            this.ravenIssue = <IRavenIssue>{
                id: "0",
                ravenPriorityId: null,
                ravenPriority: null,
                ravenCategories: [],
                ravenCategoriesIds: [],
                ravenLocationId: null,
                ravenLocation: null,
                ravenLogId: null,
                ravenLog: null,
                ravenStatus: null,
                ravenStatusId: null,
                referenceNumber: null,
                ravenNotes: this.showNotes ? RavenNotePlaceholder : null
            };
            this.resetFilter();
            this.notesInput = this.ravenIssue.ravenNotes;
            this.onChange(this.ravenIssue);
            if (!this.skipDefaultEvent) {
                this.applyDefaultEvent(generation);
            }
        } else {
            this.ravenIssue = obj;
            this.applySavedIssue(obj, generation);
        }
    }

    validate(_: AbstractControl): ValidationErrors {
        if (!this.ravenModuleEnabled || !this.ravenValidConfig) {
            return { invalid: true };
        }

        if (
            !this.priorityInput ||
            !this.categoriesInput ||
            this.categoriesInput.length === 0 ||
            !this.selectedEvent ||
            !this.locationInput
        ) {
            return { invalid: true };
        }

        return null;
    }

    reset(obj?: IRavenIssue) {
        this.writeValue(obj);
    }

    private showWarningIfBadRavenConfig() {
        if (!this.ravenValidConfig) {
            this.messageService.add(Message.Warning("Raven configuration is not valid. Please check", "Raven Configuration"));
        }
    }

    changesOnlineStatus(data) {
        this.isOnline = data.isAppOnline;
    }

    private resetFilter() {
        this.priorityInput = null;
        this.categoriesInput = null;
        this.selectedEvent = null;
        this.locationInput = null;
        this.consentInput = null;
        this.notesInput = null;
    }

    private async applySavedIssue(ravenIssue: IRavenIssue, generation: number): Promise<void> {
        this.ravenIssueId = ravenIssue.id;

        await this.loadOptions();
        if (generation !== this.writeGeneration) {
            return;
        }

        if (ravenIssue.ravenLog?.archived) {
            this.eventsOptions.push(ravenIssue.ravenLog);
            this.archivedLog = true;
        }

        const logId = ravenIssue.ravenLogId ?? ravenIssue.ravenLog?.id;

        this.isIssueClosed = ravenIssue.ravenStatus?.name === "Closed";

        // PrimeNG dropdowns/multiselects match by object identity (or dataKey against options).
        // Resolve saved values to the loaded option instances so fields rehydrate on edit.
        this.categoriesInput = this.resolveCategories(ravenIssue.ravenCategories);
        this.ravenIssue.ravenCategories = this.categoriesInput;
        this.ravenIssue.ravenCategoriesIds = (this.categoriesInput ?? []).map(it => it.id);

        this.priorityInput = this.resolveOption(this.priorityOptions, ravenIssue.ravenPriorityId, ravenIssue.ravenPriority);
        this.ravenIssue.ravenPriority = this.priorityInput;
        this.ravenIssue.ravenPriorityId = this.priorityInput?.id ?? this.ravenIssue.ravenPriorityId;

        this.selectedEvent = this.resolveOption(this.eventsOptions, logId, ravenIssue.ravenLog);
        this.ravenIssue.ravenLog = this.selectedEvent;
        this.ravenIssue.ravenLogId = this.selectedEvent?.id ?? this.ravenIssue.ravenLogId;

        this.consentInput = this.consentOptions.find(x => x.value == ravenIssue.patientConsent);

        await this.getRavenLocationsByLog(logId);
        if (generation !== this.writeGeneration) {
            return;
        }

        this.locationInput = this.resolveOption(this.locationsOptions, ravenIssue.ravenLocationId, ravenIssue.ravenLocation);
        this.ravenIssue.ravenLocation = this.locationInput;
        this.ravenIssue.ravenLocationId = this.locationInput?.id ?? this.ravenIssue.ravenLocationId;

        if (this.showNotes && !this.ravenIssue.ravenNotes) {
            this.ravenIssue.ravenNotes = RavenNotePlaceholder;
        }
        this.notesInput = this.ravenIssue.ravenNotes;

        this.onChange(this.ravenIssue);
        this.cd.detectChanges();
    }

    private _optionsReady: Promise<void> | null = null;

    private loadOptions(): Promise<void> {
        if (!this._optionsReady) {
            this._optionsReady = this.loadAllOptions();
        }
        return this._optionsReady;
    }

    private async loadAllOptions(): Promise<void> {
        if (!this.ravenApiService.isRavenModuleEnabled()) {
            return;
        }
        await Promise.all([this.loadEvents(), this.loadPriorities(), this.loadCategories()]);
        this.defaultEventId = await this.ravenApiService.isThereADefaultLog();
        this.defaultEvent = this.defaultEventId === "-1";
    }

    private async loadEvents(): Promise<void> {
        this.events = (await this.ravenApiService.getAllEvents()) as IRavenLog[];
        this.eventsOptions = [...this.events].sort(this.byField("name"));
    }

    private async applyDefaultEvent(generation: number): Promise<void> {
        await this.loadOptions();
        if (generation !== this.writeGeneration) {
            return;
        }

        if (this._episodeId || this.defaultEventId === "-1") {
            return;
        }

        this.selectedEvent = this.eventsOptions.find(x => x.id === this.defaultEventId);
        if (!this.selectedEvent) {
            return;
        }

        this.ravenIssue.ravenLog = this.selectedEvent;
        this.ravenIssue.ravenLogId = this.selectedEvent.id;

        await this.getRavenLocationsByLog(this.selectedEvent.id);
        if (generation !== this.writeGeneration) {
            return;
        }

        this.locationInput = null;

        this.onChange(this.ravenIssue);
    }

    async getRavenLocationsByLog(logId: string) {
        if (!logId) {
            this.locations = [];
            this.locationsOptions = [];
            return;
        }

        let result = await this.ravenApiService.getLocationsByLog(logId);
        this.locations = [...((result as IRavenLocation[]) ?? [])];
        this.locations.sort(this.byField("name"));
        this.locationsOptions = this.locations;
    }

    getRavenLocationsData() {
        this.ravenApiService.getAllLocations().then(result => {
            this.locations = (result as IRavenLocation[]) ?? [];
            for (let i = 0; i < this.locations.length; i++) {
                this.locationsOptions.push(this.locations[i]);
            }
            this.locationsOptions.sort(this.byField("name"));
        });
    }

    private async loadPriorities(): Promise<void> {
        this.allPriorities = (await this.priorityService.getAllPriorities()) as IRavenPriority[];
        this.priorityOptions = [...this.allPriorities].sort(this.byField("name"));
    }

    private async loadCategories(): Promise<void> {
        this.allCategories = (await this.categoryService.getAllCategories()) as IRavenCategory[];
        this.categoryOptions = this.allCategories.filter(c => c.enabled).sort(this.byField("fullName"));
    }

    getRavenStatus() {
        this.ravenApiService.checkStatuses().finally(() => {
            this.statusService.getPage(<IQueryCommand>{}).then(result => {
                this.ravenStatuses = result.items as IRavenStatus[];
                if (!this.ravenIssue || this.ravenIssue.ravenStatus) {
                    return;
                }

                const openStatus = this.ravenStatuses.find(x => x.name === "Open");
                if (!openStatus) {
                    return;
                }

                this.ravenIssue.ravenStatus = openStatus;
                this.ravenIssue.ravenStatusId = openStatus.id;

                // A saved incident already owns the form. Pushing this placeholder back
                // on a slow network overwrites the edit values (categories/priority/location).
                if (!this.ravenIssue.id || this.ravenIssue.id === "0") {
                    this.onChange(this.ravenIssue);
                }
            });
        });
    }

    setDefaultEvent() {
        if (this.defaultEvent && this._selectedEvent.id !== this.defaultEventId) {
            this.confirmationService.confirm({
                message: "Are you sure you want to change the default event?",
                accept: () => {
                    this.ravenIssue.ravenLog = this.selectedEvent;
                    this.ravenIssue.ravenLogId = this.selectedEvent.id;
                    this.ravenApiService.updateDefaultLog(this.ravenIssue.ravenLog);
                    this.defaultEventId = this.selectedEvent.id;
                },
                reject: () => {
                    this.defaultEvent = false;
                }
            });
        }
    }

    filterPriorityChanged(searchTerm: string) {
        this.priorityOptions = this.allPriorities.filter(p =>
            p.name.toLowerCase().includes(searchTerm.toLowerCase())
        ).sort(this.byField("name"));
    }

    filterCategoryChanged(searchTerm: string) {
        searchTerm = searchTerm.toLowerCase();
        this.categoryOptions = this.allCategories
            .filter(c => c.fullName.toLowerCase().indexOf(searchTerm) >= 0)
            .sort(this.byField("fullName"));
    }

    filterLocationChanged(searchTerm: string) {
        const searchTermNormalized = searchTerm.toLowerCase();

        this.locationsOptions = this.locations.filter(location =>
            location.name.toLowerCase().includes(searchTermNormalized)
        ).sort(this.byField("name"));
    }

    eventChanged(newSelectedEvent: IRavenLog) {
        this.selectedEvent$.next(newSelectedEvent);
        // Sometimes select an Event randomly doesn't show information in the dropdown so we force a detect Changes:
        this.cd.detectChanges();
    }

    async doProcessEventChanged(newSelectedEvent: IRavenLog) {
        if (this.defaultEvent && this.defaultEventId !== "-1") {
            this.confirmationService.confirm({
                message: "Are you sure you want to change the default event?",
                accept: () => {
                    this._updateLogAndLocation(newSelectedEvent);
                    this._updateDefaultLog();
                    this.onChange(this.ravenIssue);
                },
                reject: () => {
                    this.selectedEvent = this.ravenIssue.ravenLog;
                    this.onChange(this.ravenIssue);
                }
            });
        } else if (this.defaultEvent && this.defaultEventId === "-1") {
            this._updateLogAndLocation(newSelectedEvent);
            this._updateDefaultLog();
            this.onChange(this.ravenIssue);
        } else {
            this._updateLogAndLocation(newSelectedEvent);
            if (this.defaultEvent) {
                this._updateDefaultLog();
            }
            this.onChange(this.ravenIssue);
        }
    }

    _updateLogAndLocation(newSelectedEvent: IRavenLog) {
        this.ravenIssue.ravenLog = newSelectedEvent;
        this.ravenIssue.ravenLogId = newSelectedEvent.id;

        this.locationInput = null;
        this.ravenIssue.ravenLocation = null;
        this.ravenIssue.ravenLocationId = null;

        this.getRavenLocationsByLog(newSelectedEvent.id);
    }

    _updateDefaultLog() {
        this.ravenApiService.updateDefaultLog(this.ravenIssue.ravenLog);
        this.defaultEventId = this.selectedEvent.id;
    }

    priorityChanged(newPriorityInput: IRavenPriority) {
        this.ravenIssue.ravenPriority = newPriorityInput;
        this.ravenIssue.ravenPriorityId = newPriorityInput.id;
        this.onChange(this.ravenIssue);
    }

    categoriesChanged(newCategoriesInput: IRavenCategory[]) {
        if (newCategoriesInput.length === 0) {
            this.locationInput = <any>null;
            this.ravenIssue.ravenLocation = <any>null;
            this.ravenIssue.ravenLocationId = <any>null;
        }
        this.ravenIssue.ravenCategories = newCategoriesInput;
        this.ravenIssue.ravenCategoriesIds = newCategoriesInput.map(it => it.id);
        this.onChange(this.ravenIssue);
    }

    locationChanged(newLocationInput: IRavenLocation) {
        if (newLocationInput !== null) {
            this.ravenIssue.ravenLocation = newLocationInput;
            this.ravenIssue.ravenLocationId = newLocationInput.id;
            this.onChange(this.ravenIssue);
        }
    }

    consentChanged(newConsentInput: { label: string; value: boolean }) {
        if (newConsentInput !== null) {
            this.ravenIssue.patientConsent = newConsentInput.value;
            this.onChange(this.ravenIssue);
        }
    }

    notesChanged(value: string) {
        this.ravenIssue.ravenNotes = value;
        this.onChange(this.ravenIssue);
    }

    private resolveCategories(categories: IRavenCategory[] | null | undefined): IRavenCategory[] {
        if (!categories?.length) {
            return [];
        }

        return categories.map(category => {
            const match = this.categoryOptions.find(o => String(o.id) === String(category.id));
            if (match) {
                return match;
            }

            // Keep saved (e.g. disabled) categories visible in the multiselect
            this.categoryOptions = [...this.categoryOptions, category];
            return category;
        });
    }

    private resolveOption<T extends { id: string }>(options: T[], id: string, fallback: T | null | undefined): T | null {
        const sameId = (optionId: string) => id != null && id !== "" && String(optionId) === String(id);

        if (id != null && id !== "") {
            const match = options.find(o => sameId(o.id));
            if (match) {
                return match;
            }
        }

        if (fallback?.id) {
            const match = options.find(o => String(o.id) === String(fallback.id));
            if (match) {
                return match;
            }

            options.push(fallback);
            return fallback;
        }

        return fallback ?? null;
    }

    private byField<T extends { [k in K]: string }, K extends keyof T>(field: K) {
        return (a: T, b: T) => ((a[field] ?? "").toLowerCase() > (b[field] ?? "").toLowerCase() ? 1 : -1);
    }
}
